import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Search, 
  Filter, 
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  User,
  Calendar
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function Concerns() {
  const { user } = useAuth();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewConcern, setShowNewConcern] = useState(false);
  const [newConcern, setNewConcern] = useState({ title: '', description: '', priority: 'medium', category: 'general' });

  useEffect(() => {
    fetchConcerns();
  }, []);

  const fetchConcerns = async () => {
    try {
      const data = await apiRequest('/concerns');
      setConcerns(data || []);
    } catch (error) {
      console.error('Failed to fetch concerns:', error);
      // Mock data for demo
      setConcerns([
        { id: '1', title: 'Staff shortage on weekends', description: 'Need more staff for weekend shifts', status: 'open', priority: 'high', category: 'staffing', created_by: 'Store Manager', created_at: new Date().toISOString() },
        { id: '2', title: 'Training materials outdated', description: 'The training videos need updating', status: 'in_progress', priority: 'medium', category: 'training', created_by: 'HR Manager', created_at: new Date().toISOString() },
        { id: '3', title: 'Equipment maintenance required', description: 'Forklift needs servicing', status: 'resolved', priority: 'low', category: 'equipment', created_by: 'Warehouse Admin', created_at: new Date().toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitConcern = async (e) => {
    e.preventDefault();
    try {
      await apiRequest('/concerns', {
        method: 'POST',
        body: JSON.stringify(newConcern)
      });
      toast.success('Concern submitted successfully');
      setShowNewConcern(false);
      setNewConcern({ title: '', description: '', priority: 'medium', category: 'general' });
      fetchConcerns();
    } catch (error) {
      toast.error('Failed to submit concern');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || styles.open;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-700',
      medium: 'bg-orange-100 text-orange-700',
      low: 'bg-green-100 text-green-700'
    };
    return styles[priority] || styles.medium;
  };

  const filteredConcerns = concerns.filter(c => {
    const matchesSearch = !searchQuery || 
      c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="concerns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Staff Concerns</h1>
          <p className="text-sm text-slate-500">Track and manage staff concerns and issues</p>
        </div>
        <Button onClick={() => setShowNewConcern(true)} className="bg-red-600 hover:bg-red-700 gap-2">
          <Plus size={16} />
          Raise Concern
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search concerns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-700">{concerns.filter(c => c.status === 'open').length}</p>
            <p className="text-sm text-yellow-600">Open</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{concerns.filter(c => c.status === 'in_progress').length}</p>
            <p className="text-sm text-blue-600">In Progress</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{concerns.filter(c => c.status === 'resolved').length}</p>
            <p className="text-sm text-green-600">Resolved</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-700">{concerns.length}</p>
            <p className="text-sm text-slate-600">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Concerns List */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-600" />
            All Concerns ({filteredConcerns.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredConcerns.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <AlertTriangle size={40} className="mx-auto mb-3 opacity-50" />
              <p>No concerns found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredConcerns.map((concern) => (
                <div key={concern.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-slate-900 truncate">{concern.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(concern.status)}`}>
                          {concern.status?.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(concern.priority)}`}>
                          {concern.priority}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 line-clamp-2">{concern.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {concern.created_by}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {new Date(concern.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <MessageSquare size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Concern Modal */}
      {showNewConcern && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="border-b">
              <CardTitle>Raise a Concern</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleSubmitConcern} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Title</label>
                  <Input
                    value={newConcern.title}
                    onChange={(e) => setNewConcern({...newConcern, title: e.target.value})}
                    placeholder="Brief title for the concern"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <textarea
                    value={newConcern.description}
                    onChange={(e) => setNewConcern({...newConcern, description: e.target.value})}
                    placeholder="Describe the concern in detail..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[100px]"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Priority</label>
                    <select
                      value={newConcern.priority}
                      onChange={(e) => setNewConcern({...newConcern, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Category</label>
                    <select
                      value={newConcern.category}
                      onChange={(e) => setNewConcern({...newConcern, category: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="general">General</option>
                      <option value="staffing">Staffing</option>
                      <option value="training">Training</option>
                      <option value="equipment">Equipment</option>
                      <option value="safety">Safety</option>
                      <option value="policy">Policy</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowNewConcern(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                    Submit
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
